import React, { Suspense } from 'react';
import { createBrowserHistory } from 'history';
import { Route, Switch } from 'react-router-dom';
import { useDispatch, useStore } from 'react-redux';
import { lazy } from '@loadable/component';
import * as paths from 'constants/paths';
import { NavigationGuard } from 'components/NavigationGuard';
import { Analytics } from 'utils/analytics';
import { redirectToOriginApplication } from 'actionCreators/navigation';
import { sendScreenDetailsToMobile } from 'utils/mobileUtils';
import { setQueryParam } from 'actionCreators/config';

const history = createBrowserHistory();

/**
 * @todo Replace with a real "GlobalLoader" component
 */
const Fallback = () => <div>{`Loading...`}</div>;
/**
 *
 * @param {*} screen
 */
function getScreen(screen) {
  return lazy(() =>
    import(
      /* webpackChunkName: "[request]" */
      /* webpackExclude: /(((Screen|Container|.test|.submit|.validate).jsx?)|.snap)$/ */ `./components/screens/${screen}`
    ).catch((error) => error)
  );
}

/*routes for journey
Please add routes as per journey. Add exceptional journey at the end like speak advisor, sign in
*/
export const routes = [
  { path: paths.ROOT, component: getScreen('Home') },

  { path: paths.ABOUT_YOUR_APPLICATION, component: getScreen('AboutApplication') },
  { path: paths.YOUR_LOAN_OPTIONS, component: getScreen('CreateLoan') },
  { path: paths.YOUR_LOAN_PURPOSE, component: getScreen('LoanOptions') },

  { path: paths.CONSOLIDATE_YOUR_DEBT, component: getScreen('DebtConsolidation') },

  { path: paths.SOLE_INITIAL_QUESTIONS, component: getScreen('InitialHomePage') },

  { path: paths.USE_OF_PERSONAL_INFORMATION, component: getScreen('PersonalInformation') },

  { path: paths.YOUR_FINANCIAL_CIRCUMSTANCES, component: getScreen('FinancialCircumstances') },

  {
    path: paths.YOUR_CHANGING_FINANCIAL_CIRCUMSTANCES,
    component: getScreen('ChangeFinancialCircumstances')
  },

  { path: paths.CREDIT_CARD_DETAILS, component: getScreen('CreditCardDetails') },

  { path: paths.EMPLOYMENT_DETAILS, component: getScreen('EmploymentDetails') },
  { path: paths.CHOOSE_A_USERNAME, component: getScreen('ChooseUserName') },

  { path: paths.YOUR_MAIN_BANK_ACCOUNT, component: getScreen('YourMainBankAccount') },

  { path: paths.DIRECT_DEBIT_INSTRUCTIONS, component: getScreen('DirectDebitInstructions') },

  { path: paths.REVIEW_YOUR_LOAN_DETAILS, component: getScreen('ReviewLoanDetails') },

  { path: paths.INFORMATION_ABOUT_YOUR_LOAN, component: getScreen('LoanInformation') },
  { path: paths.YOUR_LOAN_APPROVAL, component: getScreen('LoanApproval') },
  { path: paths.YOUR_LOAN_AGREEMENT, component: getScreen('TakeYourLoan') },
  { path: paths.YOUR_LOAN_AGREEMENT_POST_CONTRACT, component: getScreen('TakeYourLoanAgreement') },
  { path: paths.YOUR_LOAN_AGREEMENT_PRE_CONTRACT, component: getScreen('TakeYourLoanPreContract') },

  { path: paths.CHECKING_YOUR_IDENTITY, component: getScreen('CheckingIdentity') },

  { path: paths.VERIFYING_YOUR_IDENTITY, component: getScreen('IdentityVerification') },
  { path: paths.CONGRATULATIONS, component: getScreen('Congratulations') },

  {
    path: paths.JOINT_APPLICATION_SUBMISSION,
    component: getScreen('JointApplicationSubmission')
  },

  { path: paths.JOINT_INITIAL_QUESTION, component: getScreen('JointInitialQuestion') },

  { path: paths.YOUR_SIGNATURE_IS_NEEDED, component: getScreen('NeedSignature') },

  { path: paths.ID_CHECK_CAN_NOT_DONE, component: getScreen('IdCheckNotDone') },

  { path: paths.CREDIT_CHECK_DECLINE, component: getScreen('CreditCheckDecline') },

  { path: paths.CREDIT_CHECK_REFERRED, component: getScreen('CreditCheckReferred') },
  {
    path: paths.CREDIT_CHECK_REFERRED_NO_MATCH,
    component: getScreen('CreditCheckReferredNoMatch')
  },
  {
    path: paths.CREDIT_CHECK_REFERRED_BANK_CHECK,
    component: getScreen('CreditCheckReferredBankCheck')
  },
  { path: paths.CREDIT_CHECK_REFERRED_POI, component: getScreen('CreditCheckReferredPOI') },
  { path: paths.CONFIRM_AUTH_CODE, component: getScreen('ConfirmAuthCode') },
  {
    path: paths.CREDIT_CHECK_REFERRED_BANK_CHECK_POI,
    component: getScreen('CreditCheckReferredBankCheckPOI')
  },
  { path: paths.GET_IN_TOUCH, component: getScreen('GetInTouch') },
  { path: paths.CONFIRM_CODE, component: getScreen('ConfirmCode') },
  { path: paths.DOCUMENT_RECEIVED, component: getScreen('DocumentReceived') },
  { path: paths.INFORM_CHOICE, component: getScreen('InformedChoice') },
  { path: paths.ABOUT_YOUR_LOAN_INFO, component: getScreen('AboutYourLoanInfo') },
  { path: paths.INTERNAL_DEBT_CONSOLIDATE, component: getScreen('InternalDebtConsolidation') },
  { path: paths.BEFORE_YOUR_SCREEN, component: getScreen('BeforeScreen') },
  { path: paths.INELIGIBILTY_SCREEN, component: getScreen('IneligibilityScreen') },
  { path: paths.ACCOUNT_LIST_SCREEN, component: getScreen('AccountList') },
  { path: paths.DOCUMENTS_ON_WAY, component: getScreen('DocumentsOnWay') },
  { path: paths.JOINT_BEFORE_YOUR_SCREEN, component: getScreen('JointBeforeScreen') },
  { path: paths.CONFIRM_JOINT_LINK_CODE, component: getScreen('ConfirmJointLinkCode') },
  { path: paths.SUBMISSION_HOLDING_SCREEN, component: getScreen('SubmissionHoldingScreen') },
  { path: paths.WARNING_MESSAGE_SCREEN, component: getScreen('WarningMessage') },

  // Customer Information Screens
  { path: paths.YOUR_PERSONAL_DETAILS, component: getScreen('YourPersonalDetails') },
  { path: paths.YOUR_ADDRESS_DETAILS, component: getScreen('YourAddressDetails') },
  { path: paths.YOUR_CONTACT_DETAILS, component: getScreen('YourContactDetails') },
  { path: paths.YOUR_EMPLOYMENT_DETAILS, component: getScreen('YourEmploymentDetails') },
  { path: paths.INCOME_AND_EXPENSES, component: getScreen('YourIncomeAndExpenses') },
  { path: paths.MARKETING_PREFERENCES, component: getScreen('YourMarketingPreferences') },
  { path: paths.REVIEW_YOUR_DETAILS, component: getScreen('ReviewYourDetails') },
  { path: paths.DUPLICATE_CUSTOMER_ERROR, component: getScreen('DuplicateCustomerError') },
  /** Decision In Principle Screens */
  { path: paths.DIP_PERSONAL_DETAILS, component: getScreen('PersonalDetailsDIP') },
  { path: paths.DIP_ADDRESS_DETAILS, component: getScreen('AddressDetailsDIP') },
  { path: paths.DIP_EMPLOYMENT_DETAILS, component: getScreen('EmploymentDetailsDIP') },
  { path: paths.DIP_ABOUT_THIS_CHECK, component: getScreen('AboutThisCheckDIP') },
  { path: paths.DIP_GREAT_NEWS, component: getScreen('GreatNewsDIP') },
  { path: paths.DIP_UNLIKELY_RESULT, component: getScreen('UnlikelyScreenDIP') },
  { path: paths.DIP_NO_MATCH, component: getScreen('NoMatchScreenDIP') },
  { path: paths.DIP_REVIEW_DETAILS, component: getScreen('ReviewDetailsDIP') },
  { path: paths.DIP_YOUR_LOAN_OPTIONS, component: getScreen('CreateLoan') },
  { path: paths.DIP_YOUR_LOAN_PURPOSE, component: getScreen('LoanOptions') },
  { path: paths.DIP_CONTACT_DETAILS, component: getScreen('ContactDetailsDIP') },
  { path: paths.DIP_MARKETING_PREFERENCES, component: getScreen('MarketingPreferencesDIP') },
  { path: paths.DIP_YOUR_MAIN_BANK_ACCOUNT, component: getScreen('YourMainBankAccount') },
  { path: paths.DIP_DIRECT_DEBIT_INSTRUCTIONS, component: getScreen('DirectDebitInstructions') },
  { path: paths.DIP_REVIEW_YOUR_LOAN_DETAILS, component: getScreen('ReviewLoanDetails') },
  { path: paths.DIP_INFORMATION_ABOUT_YOUR_LOAN, component: getScreen('LoanInformation') },
  { path: paths.NTB_DIP_HOOYU_DRAWDOWN, component: getScreen('HooYuDrawdown') },
  { path: paths.NTB_DIP_HOOYU_UNAVAILABLE, component: getScreen('HooYuUnavailable') },
  { path: paths.NTB_DIP_KEEPING_YOU_SAFE, component: getScreen('KeepingYouSafe') },
  { path: paths.NTB_DIP_KEY_FEATURES, component: getScreen('KeyFeaturesNTB') },
  {
    path: paths.NTB_DIP_DECLARATION_AND_AUTHORISATION,
    component: getScreen('DeclarationAndAuthorisation')
  },
  {
    path: paths.NTB_DIP_IDV_DECLINE,
    component: getScreen('IdentityCheckDecline')
  },
  /** NTB Post-DIP Review Details Screens */
  { path: paths.POST_DIP_REVIEW_DETAIL, component: getScreen('PostDIPReviewDetails') },
  {
    path: paths.POST_DIP_EDIT_EMPLOYMENT_DETAILS,
    component: getScreen('PostDIPEmploymentDetails')
  },
  { path: paths.POST_DIP_EDIT_ADDRESS_DETAILS, component: getScreen('PostDIPAddressDetails') },
  { path: paths.POST_DIP_EDIT_INCOME_DETAILS, component: getScreen('PostDIPIncomeDetails') },
  { path: paths.POST_DIP_EDIT_PERSONAL_DETAILS, component: getScreen('PostDIPPersonalDetails') },
  { path: paths.POST_DIP_EDIT_CONTACT_DETAILS, component: getScreen('PostDIPContactDetails') },
  /** User timed out error screen */
  { path: paths.USER_TIMED_OUT, component: getScreen('TimedOutError') }
];

export const customerInformationPaths = {
  personalDetails: paths.YOUR_PERSONAL_DETAILS,
  addressDetails: paths.YOUR_ADDRESS_DETAILS,
  contactDetails: paths.YOUR_CONTACT_DETAILS,
  employmentDetails: paths.YOUR_EMPLOYMENT_DETAILS,
  financialDetails: paths.INCOME_AND_EXPENSES
};

const AppRouter = ({ location, action, lockNavigation, goForward, goBack }) => {
  const {
    navigation: { originPath },
    config,
    exitModalOpen,
    config: { accessToken },
    navigation
  } = useStore().getState();

  const dispatch = useDispatch();

  function handleLocationChange({ pathname }) {
    dispatch(Analytics.send());
    sendScreenDetailsToMobile(accessToken, pathname, config, exitModalOpen, navigation);
  }

  function onRedirectToOrigin() {
    dispatch(redirectToOriginApplication());
  }

  function onToggleBackButton(inEditMode) {
    dispatch(setQueryParam({ inEditMode }));
  }

  return (
    <NavigationGuard
      location={location}
      action={action}
      originPath={originPath}
      lockNavigation={lockNavigation}
      goForward={goForward}
      goBack={goBack}
      onLocationChange={handleLocationChange}
      onRedirectToOrigin={onRedirectToOrigin}
      toggleBackButton={onToggleBackButton}
    >
      {(locationToRender) => (
        <Suspense fallback={<Fallback />}>
          <Switch location={locationToRender}>
            {routes.map(({ path, component: ScreenComponent }) => {
              return (
                <Route key={`${path}-screen`} path={path} exact>
                  <ScreenComponent location={{ path }} />
                </Route>
              );
            })}
          </Switch>
        </Suspense>
      )}
    </NavigationGuard>
  );
};

export { AppRouter, history };
